import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PlatformLayout } from '@/components/layout/PlatformLayout';

// Landing Page
import { LandingPage } from '@/pages/LandingPage';

// Auth Pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { PlatformLoginPage } from '@/pages/platform/PlatformLoginPage';
import { TwoFactorSetup } from '@/pages/auth/TwoFactorSetup';
import { TwoFactorVerify } from '@/pages/auth/TwoFactorVerify';
import { ForcePasswordChangePage } from '@/pages/auth/ForcePasswordChangePage';
import { PlatformForcePasswordChangePage } from '@/pages/platform/PlatformForcePasswordChangePage';

// Institution Pages
import { DashboardPage } from '@/pages/institution/DashboardPage';
import { ExecutiveDashboardPage } from '@/pages/institution/ExecutiveDashboardPage';
import { DigitizationPage } from '@/pages/institution/DigitizationPage';
import { SmartRegistryPage } from '@/pages/institution/SmartRegistryPage';
import { StaffPortalPage } from '@/pages/institution/StaffPortalPage';
import { InterMDATransferPage } from '@/pages/institution/InterMDATransferPage';
import { SecurityAuditPage } from '@/pages/institution/SecurityAuditPage';
import { CompliancePage } from '@/pages/institution/CompliancePage';
import { RecordsArchivalPage } from '@/pages/institution/RecordsArchivalPage';
import { RetirementProcessingPage } from '@/pages/institution/RetirementProcessingPage';
import { RecordsPage } from '@/pages/institution/RecordsPage';
import { PFilesPage } from '@/pages/institution/PFilesPage';
import { UsersPage } from '@/pages/institution/UsersPage';
import { DepartmentsPage } from '@/pages/institution/DepartmentsPage';
import { TasksPage } from '@/pages/institution/TasksPage';
import { WorkflowsPage } from '@/pages/institution/WorkflowsPage';
import { RolesPage } from '@/pages/institution/RolesPage';
import { RecycleBinPage } from '@/pages/institution/RecycleBinPage';
import { SettingsPage } from '@/pages/institution/SettingsPage';
import { SearchPage } from '@/pages/institution/SearchPage';
import { ReportsPage } from '@/pages/institution/ReportsPage';
import { NotificationsPage } from '@/pages/institution/NotificationsPage';
import { ProfilePage } from '@/pages/institution/ProfilePage';

// Platform Pages
import { PlatformDashboardPage } from '@/pages/platform/PlatformDashboardPage';
import { MDAsPage } from '@/pages/platform/MDAsPage';
import { PlatformUsersPage } from '@/pages/platform/PlatformUsersPage';
import { PlatformConfigPage } from '@/pages/platform/PlatformConfigPage';
import { AnalyticsPage } from '@/pages/platform/AnalyticsPage';
import { AuditLogsPage } from '@/pages/platform/AuditLogsPage';
import { AnnouncementsPage } from '@/pages/platform/AnnouncementsPage';
import { PlatformStaffServicePage } from '@/pages/platform/PlatformStaffServicePage';
import { SupportDashboardPage } from '@/pages/platform/SupportDashboardPage';

// Protected route wrapper
import { ProtectedRoute } from './ProtectedRoute';
import { DashboardRedirect } from './DashboardRedirect';

export function AppRouter() {
  return (
    <Routes>
      {/* Landing Page */}
      <Route path="/welcome" element={<LandingPage />} />

      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/platform/login" element={<PlatformLoginPage />} />
      <Route path="/2fa/setup" element={<TwoFactorSetup />} />
      <Route path="/2fa/verify" element={<TwoFactorVerify />} />
      <Route path="/force-password-change" element={<ForcePasswordChangePage />} />
      <Route path="/platform/force-password-change" element={<PlatformForcePasswordChangePage />} />

      {/* Institution Portal Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout title="OHCS RMS" />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardRedirect />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="records" element={<RecordsPage />} />
        <Route path="records/:folderId" element={<RecordsPage />} />
        <Route path="pfiles" element={<PFilesPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="recycle-bin" element={<RecycleBinPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="admin/users" element={<UsersPage />} />
        <Route path="admin/departments" element={<DepartmentsPage />} />
        <Route path="admin/roles" element={<RolesPage />} />
        <Route path="admin/workflows" element={<WorkflowsPage />} />
        <Route path="admin/settings" element={<SettingsPage />} />
        <Route path="digitization" element={<DigitizationPage />} />
        <Route path="smart-registry" element={<SmartRegistryPage />} />
        <Route path="staff-portal" element={<StaffPortalPage />} />
        <Route path="security-audit" element={<SecurityAuditPage />} />
        <Route path="compliance" element={<CompliancePage />} />
        <Route path="records-archival" element={<RecordsArchivalPage />} />
        <Route path="retirement-processing" element={<RetirementProcessingPage />} />
      </Route>

      {/* Platform Admin Routes */}
      <Route
        path="/platform"
        element={
          <ProtectedRoute requirePlatformAdmin>
            <PlatformLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/platform/dashboard" replace />} />
        <Route path="dashboard" element={<PlatformDashboardPage />} />
        <Route path="mdas" element={<MDAsPage />} />
        <Route path="users" element={<PlatformUsersPage />} />
        <Route path="config" element={<PlatformConfigPage />} />
        <Route path="config/record-types" element={<PlatformConfigPage />} />
        <Route path="config/classifications" element={<PlatformConfigPage />} />
        <Route path="config/workflows" element={<PlatformConfigPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="audit-logs" element={<AuditLogsPage />} />
        <Route path="announcements" element={<AnnouncementsPage />} />
        <Route path="staff-service" element={<PlatformStaffServicePage />} />
        <Route path="executive" element={<ExecutiveDashboardPage />} />
        <Route path="staff-transfers" element={<InterMDATransferPage />} />
        <Route path="retirement" element={<RetirementProcessingPage />} />
        <Route path="praad-transfers" element={<RecordsArchivalPage />} />
        <Route path="support" element={<SupportDashboardPage />} />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
